import { useMemo } from "react";

import { createTheme } from "../../../styles";
import { colors } from "../../../styles/colors";

/**
 * Builds a Stitches theme class from a draft tenant palette, so a subtree can
 * be rendered exactly as that tenant's portal renders (EN-5154).
 *
 * This is `utils/theme.ts`'s `applyColorsPatch` with one difference: that one
 * puts the class on `<html>` for the whole app at login, and this returns it
 * for the caller to scope to a preview. The patch rule is deliberately the
 * same — only keys already present in `colors` take effect — so a token the
 * portal doesn't know about is ignored here exactly as it is at login.
 */
export function useTenantThemeClass(styles: Record<string, string>): string {
  // Serialised so the theme is rebuilt only when a value actually changes,
  // not on every render of the editor.
  const signature = JSON.stringify(styles);

  return useMemo(() => {
    const patched: Record<string, string> = { ...colors };

    Object.entries(styles).forEach(([token, value]) => {
      if (
        Object.prototype.hasOwnProperty.call(patched, token) &&
        typeof value === "string"
      ) {
        patched[token] = value.trim();
      }
    });

    return String(createTheme({ colors: patched }));
    // Keyed on `signature`, not `styles`: the editor hands a fresh object every
    // render, which would rebuild the theme on each keystroke.
  }, [signature]);
}
